import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { List } from '@gaiads/telia-react-component-library';
import { AdminCategory, AdminSubcategory, SubcategoryTranslation } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { EditableItemInputProps } from '../../../../shared/EditableItem/EditableItemInput/EditableItemInput';
import { RootState } from '../../../../store';
import { selectEditingLocaleId, selectReferenceLocaleId } from '../../../../store/editingLocale';
import { selectLocaleById } from '../../../../store/locales';
import { resetNotification, setNotification } from '../../../../store/ui';
import { getInitialSubcategoryTranslation } from '../../../../utils/categories';
import { removeEntity, updateEntity, upsertEntity } from '../../../../utils/entities';
import { defaultLocale, getLanguageFromLocaleName } from '../../../../utils/locales';
import { AdminSettingsErrorProps } from '../../AdminSettings';

interface SubCategoryItemProps extends AdminSettingsErrorProps {
  category: AdminCategory;
  subcategory: AdminSubcategory;
  isNew?: boolean;
  handleSubcategorySave?: (category: AdminSubcategory) => Promise<boolean>;
  handleNewSubcategorySave?: (category: AdminSubcategory) => Promise<boolean>;
  handleDelete?: (category: AdminCategory, subcategory: AdminSubcategory) => void;
  handleBlur?: () => void;
}

export default function SubcategoryItem(props: SubCategoryItemProps) {
  const { category, subcategory, isNew, errors, setErrors, handleSubcategorySave, handleNewSubcategorySave, handleDelete, handleBlur } = props;

  const { t } = useTranslation('adminSettings');

  const dispatch = useDispatch();

  const editingLocaleId = useSelector(selectEditingLocaleId);
  const referenceLocaleId = useSelector(selectReferenceLocaleId);

  const editingLocale = useSelector((state: RootState) => (editingLocaleId ? selectLocaleById(state, editingLocaleId) : undefined));
  const referenceLocale = useSelector((state: RootState) => (referenceLocaleId ? selectLocaleById(state, referenceLocaleId) : undefined));

  const editingTranslation = useMemo(
    () =>
      editingLocaleId
        ? subcategory.translations?.find(translation => translation.locale_id === editingLocaleId) ||
          getInitialSubcategoryTranslation(editingLocaleId)
        : undefined,
    [subcategory, editingLocaleId],
  );
  const referenceTranslation = useMemo(
    () =>
      !isNew && referenceLocaleId
        ? subcategory.translations?.find(translation => translation.locale_id === referenceLocaleId) ||
          getInitialSubcategoryTranslation(referenceLocaleId)
        : subcategory,
    [subcategory, referenceLocaleId, isNew],
  );

  const editingLanguage = getLanguageFromLocaleName(editingLocale?.name);
  const referenceLanguage = getLanguageFromLocaleName(referenceLocale?.name);
  const fallbackLanguage = defaultLocale.name_in_native_language;

  const handleSave = async (name?: AdminSubcategory['name']): Promise<boolean> => {
    if (name && name !== '') {
      setErrors({
        subcategories: undefined,
      });
      dispatch(resetNotification());
      const updatedSubcategory: AdminSubcategory = {
        ...subcategory,
        name,
      };
      const subcategories = upsertEntity(category.subcategories, updatedSubcategory);
      const updatedCategory: AdminCategory = {
        ...category,
        subcategories,
      };
      if (!isNew) {
        return handleSubcategorySave?.(updatedCategory) || false;
      } else {
        return handleNewSubcategorySave?.(updatedCategory) || false;
      }
    } else {
      setErrors({
        subcategories: {
          [subcategory.id]: t('productCategories.errors.nameBlank'),
        },
      });
      if (!isNew) {
        dispatch(
          setNotification({
            text: t('productCategories.errors.updateSubcategory'),
            show: true,
            type: 'warning',
          }),
        );
      } else {
        dispatch(
          setNotification({
            text: t('productCategories.errors.createSubcategory'),
            show: true,
            type: 'warning',
          }),
        );
      }
      return false;
    }
  };

  const handleTranslationSave = async (translation?: SubcategoryTranslation, name?: SubcategoryTranslation['name']): Promise<boolean> => {
    if (translation) {
      let translations;
      if (name && name !== '') {
        const updatedTranslation = {
          ...translation,
          name,
        };
        translations = upsertEntity(subcategory.translations, updatedTranslation);
      } else {
        translations = removeEntity(subcategory.translations, translation);
      }
      const updatedSubcategory = {
        ...subcategory,
        translations,
      };
      const subcategories = updateEntity(category.subcategories, updatedSubcategory);
      const updatedCategory: AdminCategory = {
        ...category,
        subcategories,
      };
      return handleSubcategorySave?.(updatedCategory) || false;
    } else return false;
  };

  const handleSubcategoryBlur = () => {
    setErrors({
      subcategories: undefined,
    });
    if (handleBlur) handleBlur();
  };

  const onSave: EditableItemInputProps['onSave'] = name => handleTranslationSave(editingTranslation, name);
  const onReferenceSave: EditableItemInputProps['onSave'] =
    !isNew && referenceLocaleId && 'locale_id' in referenceTranslation ? name => handleTranslationSave(referenceTranslation, name) : handleSave;

  const placeholder = editingLocaleId
    ? t('productCategories.placeholders.subcategoryTranslation', { language: editingLanguage })
    : t('productCategories.placeholders.subcategory', { language: fallbackLanguage });
  const referencePlaceholder =
    !isNew && referenceLocaleId
      ? t('productCategories.placeholders.subcategoryTranslation', { language: referenceLanguage })
      : t('productCategories.placeholders.subcategory', { language: fallbackLanguage });

  return (
    <List.Item margin={{ size: 'zero' }} className="product-categories__subcategory-item">
      <EditableItem backgroundColor="gray100" backgroundHoverColor="transparent">
        <EditableItem.Input
          value={editingTranslation?.name}
          showSave
          onSave={onSave}
          onDelete={() => handleDelete && handleDelete(category, subcategory)}
          onBlur={handleSubcategoryBlur}
          showDelete
          focusOnEdit={!isNew}
          flexProps={{
            className: 'product-categories__subcategory-field',
          }}
          divProps={{
            className: 'product-categories__subcategory-content',
          }}
          inputProps={{
            placeholder,
            'data-testid': `category-${category.id}-subcategory-${isNew ? 'new' : subcategory.id}-input`,
          }}
          iconFlexProps={{
            className: 'product-categories__subcategory-icons',
          }}
          editIconProps={{
            'data-testid': `category-${category.id}-subcategory-${isNew ? 'new' : subcategory.id}-edit`,
          }}
          deleteIconProps={{
            'data-testid': `category-${category.id}-subcategory-${isNew ? 'new' : subcategory.id}-delete`,
          }}
        />
        {referenceTranslation && (
          <EditableItem.Input
            value={referenceTranslation?.name}
            isReference
            showSave
            onSave={onReferenceSave}
            edit={isNew}
            focusOnEdit={isNew}
            inputProps={{
              placeholder: referencePlaceholder,
              errorText: errors.subcategories?.[subcategory.id],
              'data-testid': `category-${category.id}-subcategory-reference-${isNew ? 'new' : subcategory.id}-input`,
            }}
            flexProps={{
              className: ['product-categories__subcategory-field', 'product-categories__subcategory-reference-field'],
            }}
            divProps={{
              className: 'product-categories__subcategory-content',
            }}
            iconFlexProps={{
              className: 'product-categories__subcategory-reference-icons',
            }}
            editIconProps={{
              'data-testid': `category-${category.id}-subcategory-reference-${isNew ? 'new' : subcategory.id}-edit`,
            }}
            deleteIconProps={{
              'data-testid': `category-${category.id}-subcategory-reference-${isNew ? 'new' : subcategory.id}-delete`,
            }}
          />
        )}
      </EditableItem>
    </List.Item>
  );
}
